<template>
  <div class="sf-gallery">
    <div class="sf-gallery__stage">
      <div
        ref="glide"
        class="glide"
      >
        <div
          class="glide__track"
          data-glide-el="track"
        >
          <ul class="glide__slides">
            <li
              v-for="(picture, index) in imagesWith3D"
              :key="'slide-' + index"
              class="glide__slide"
              @mouseover="startZoom(picture)"
              @mousemove="moveZoom($event, index)"
              @mouseout="removeZoom(index)"
            >
              <slot
                name="big-image"
                v-bind="{
                  enableZoom,
                  picture,
                  index,
                  imageWidth,
                  imageHeight,
                  imageTag,
                  nuxtImgConfig,
                }"
              >
                <ProductVideo
                  v-if="isVideo(picture)"
                  :src="getVideo(picture)"
                />
                <product3-d-viewer
                  v-else-if="is3DModel(picture)"
                  :src="get3DModel(picture)"
                  :poster="picture.desktop.url"
                  :width="'720px'"
                  :height="'500px'"
                />
                <SfImage
                  v-else
                  ref="sfGalleryBigImage"
                  class="sf-gallery__big-image"
                  :class="{ 'sf-gallery__big-image--has-zoom': enableZoom }"
                  :src="picture.desktop.url"
                  :alt="picture.alt"
                  :placeholder="picture.placeholder"
                  :width="imageWidth"
                  :height="imageHeight"
                  :image-tag="imageTag"
                  :nuxt-img-config="nuxtImgConfig"
                  @click="$emit('click:stage', { picture, index })"
                />
              </slot>
            </li>
          </ul>
        </div>
        <div v-if="sliderOptions.arrows" class="glide__arrows desktop-only">
          <button
            class="glide__arrow glide__arrow--left"
            data-glide-dir="<"
            aria-label="Previous image"
            @click="go(activeIndex - 1)"
          >
            &#10094;
          </button>
          <button
            class="glide__arrow glide__arrow--right"
            data-glide-dir=">"
            aria-label="Next image"
            @click="go(activeIndex + 1)"
          >
            &#10095;
          </button>
        </div>
      </div>
      <transition name="sf-fade">
        <div
          ref="outSide"
          :class="{
            'display-none':
              !outsideZoom || !isZoomStarted || (!outsideZoom && !enableZoom),
          }"
          :style="{ width: `${imageWidth}px`, height: `${imageHeight}px` }"
        >
          <slot
            name="outside-zoom"
            v-bind="{
              definedPicture,
              imageWidth,
              imageHeight,
              imageTag,
              nuxtImgConfig,
            }"
          >
            <SfImage
              v-if="definedPicture && definedPicture.url !== ''"
              ref="imgZoom"
              class="sf-gallery__zoom"
              :src="definedPicture.url"
              :width="imageWidth"
              :height="imageHeight"
              :lazy="false"
              :alt="definedPicture.alt"
              :placeholder="definedPicture.placeholder"
              :image-tag="imageTag"
              :nuxt-img-config="nuxtImgConfig"
            />
          </slot>
        </div>
      </transition>
    </div>
    <div class="sf-gallery__thumbs">
      <slot
        name="thumbs"
        v-bind="{ images: imagesWith3D, active: activeIndex, go }"
      >
        <SfButton
          v-for="(image, index) in imagesWith3D"
          :key="'img-' + index"
          class="sf-button--pure sf-gallery__item"
          :class="{ 'sf-gallery__item--selected': index === activeIndex }"
          :aria-label="'Image ' + index"
          @click="go(index)"
        >
          <SfImage
            class="sf-gallery__thumb"
            :src="image.mobile.url"
            :alt="image.alt"
            :placeholder="image.placeholder"
            :width="thumbWidth"
            :height="thumbHeight"
            :image-tag="thumbImageTag"
            :nuxt-img-config="thumbNuxtImgConfig"
          />
        </SfButton>
      </slot>
    </div>
  </div>
</template>

<script>
import Glide from '@glidejs/glide';
import {
  SfImage,
  SfButton,
} from '@storefront-ui/vue';
import Product3DViewer from '~/modules/catalog/product/components/Product3DViewer';
import ProductVideo from '~/modules/catalog/product/components/ProductVideo';

/**
 * This is a copy of SfGallery with support for 3d models and with fix for an error
 * that if product has no images, then definedPicture contains empty string and because
 * of it NuxtImg throws an error in dev mode or only writes error to console in prod mode
 */
export default {
  name: 'SfGallery',
  components: {
    SfImage,
    SfButton,
    Product3DViewer,
    ProductVideo,
  },
  props: {
    images: {
      type: Array,
      default: () => [],
    },
    imageWidth: {
      type: [Number, String],
      default: null,
    },
    imageHeight: {
      type: [Number, String],
      default: null,
    },
    thumbWidth: {
      type: [Number, String],
      default: null,
    },
    thumbHeight: {
      type: [Number, String],
      default: null,
    },
    current: {
      type: Number,
      default: 1,
    },
    sliderOptions: {
      type: Object,
      default() {
        return {
          type: 'slider',
          autoplay: false,
          rewind: false,
          gap: 0,
        };
      },
    },
    outsideZoom: {
      type: Boolean,
      default: false,
    },
    enableZoom: {
      type: Boolean,
      default: false,
    },
    imageTag: {
      type: String,
      default: 'img',
    },
    nuxtImgConfig: {
      type: Object,
      default: () => ({}),
    },
    thumbImageTag: {
      type: String,
      default: 'img',
    },
    thumbNuxtImgConfig: {
      type: Object,
      default: () => ({}),
    },
    product3DEntries: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      positionStatic: {},
      eventHover: {},
      glide: null,
      activeIndex: this.current - 1,
      style: '',
      pictureSelected: this.images[0] || {
        alt: '',
        zoom: '',
        big: {
          url: '',
        },
        desktop: '',
        placeholder: '',
      },
      isZoomStarted: false,
    };
  },
  computed: {
    imagesWith3D () {
      return [...this.images, ...this.product3DEntries.map((entry) => ({ ...entry.product3DImage, is3DImage: true }))]
    },
    mapPictures() {
      // map images to handle picture tags with SfImage
      return this.imagesWith3D.map(({ desktop, big }) => ({
        mobile: desktop,
        desktop: big,
      }));
    },
    updatedSliderOptions() {
      return { ...this.sliderOptions, startAt: this.activeIndex };
    },
    definedPicture() {
      const picture = this.pictureSelected?.zoom || this.pictureSelected?.big || this.pictureSelected?.desktop;

      if (picture) {
        picture.alt = this.pictureSelected?.alt;
        picture.placeholder = this.pictureSelected?.placeholder;
      }

      return picture || '';
    },
  },
  mounted() {
    this.$nextTick(() => {
      // handle slider with swipe and transitions with Glide.js
      // https://glidejs.com/docs/
      if (this.imagesWith3D.length === 0) return;
      const glide = new Glide(this.$refs.glide, this.updatedSliderOptions);
      glide.on('run', () => {
        this.go(glide.index);
      });
      glide.mount();
      this.glide = glide;
    });
  },
  updated() {
    if (this.glide) {
      this.$nextTick(() => {
        this.glide.mount();
      });
    }
  },
  beforeDestroy() {
    if (this.glide) {
      this.glide.destroy();
    }
  },
  methods: {
    positionObject(index) {
      if (this.$refs.sfGalleryBigImage && this.$refs.sfGalleryBigImage[index]) {
        if (this.outsideZoom) {
          return this.$refs.glide.getBoundingClientRect();
        }
        return this.$refs.sfGalleryBigImage[
          index
        ].$el.getBoundingClientRect();
      }
      return '';
    },
    go(index) {
      if (index < 0 || index >= this.imagesWith3D.length) {
        return;
      }

      this.pictureSelected = this.imagesWith3D[index];
      if (!this.glide) return;
      this.activeIndex = index;
      /**
       * Event for current image change (`v-model`)
       * @type {Event}
       */
      this.$emit('click', index + 1);
      if (this.glide) {
        this.glide.go(`=${index}`);
      }
    },
    startZoom() {
      if (this.enableZoom) {
        this.isZoomStarted = true;
      }
    },
    moveZoom($event, index) {
      if (this.enableZoom && this.$refs.sfGalleryBigImage[index]) {
        this.eventHover = $event;
        if (this.outsideZoom) {
          this.positionStatic = this.positionObject(index);
          this.$refs.outSide.style.cssText = `position: absolute; left: ${
            $event.clientX - this.positionStatic.x
          }px; top: ${$event.clientY - this.positionStatic.y}px; z-index: 1;`;
          this.$refs.imgZoom.$el.children[0].style.cssText = 'transform: scale(2); width: 300px; height: auto;';
          this.$refs.imgZoom.$el.children[0].style.transformOrigin = `${
            $event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        } else {
          this.positionStatic = this.positionObject(index);
          this.$refs.sfGalleryBigImage[index].$el.children[0].style.cssText = 'top: 0; transform: scale(2);';
          this.$refs.sfGalleryBigImage[
            index
          ].$el.children[0].style.transformOrigin = `${
            $event.clientX - this.positionStatic.x
          }px ${$event.clientY - this.positionStatic.y}px`;
        }
      }
    },
    removeZoom(index) {
      if (this.enableZoom && this.$refs.sfGalleryBigImage[index]) {
        this.isZoomStarted = false;
        if (this.outsideZoom) return;
        this.$refs.sfGalleryBigImage[index].$el.children[0].style.transform = 'scale(1)';
      }
    },
    get3DModel(image) {
      return this.product3DEntries.find((entry) => entry.product3DImage && image.big.url === entry.product3DImage.big.url)?.product3DModel;
    },
    is3DModel(image) {
      return image.is3DImage && !!this.get3DModel(image);
    },
    getVideo(image) {
      return image?.video_content?.video_url;
    },
    isVideo(image) {
      if (!image?.video_content?.video_url) return false;

      const currentImage = this.imagesWith3D[this.activeIndex];
      return currentImage?.video_content?.video_url === image.video_content.video_url;
    },
  },
};
</script>
<style lang="scss">
@import "~@storefront-ui/shared/styles/components/molecules/SfGallery.scss";

.sf-gallery {
  .glide__arrows {
    position: absolute;
    top: 50%;
    left: 2%;
    width: 96%;
    display: flex;
    justify-content: space-between;
    transform: translateY(-50%);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .glide__arrow {
    z-index: 2;
    pointer-events: all;
    background: rgba(0, 0, 0, .4);
    border: none;
    color: #fff;
    font-size: 1.5em;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    &:hover {
      background: rgba(0, 0, 0, .6);
    }
    &--left {
      left: 10px;
    }
    &--right {
      right: 10px;
    }
  }

  &:hover {
    .glide__arrows {
      opacity: 1;
    }
  }
}
</style>
